import type { ReactNode } from 'react';

export interface ChartDataGridColumn<Row> {
  header: string;
  render: (row: Row) => ReactNode;
  numeric?: boolean;
}

/**
 * A multi-column table alternative to a chart: the same numbers the chart draws, one row per point, with an optional footer row. Collapsed by default behind
 * a native <details>, so it works from the keyboard and with a screen reader without script. Use ChartDataTable instead for a one-column breakdown.
 */
export function ChartDataGrid<Row>({
  caption, rows, rowKey, firstColumn, columns, footer,
}: {
  caption: string;
  rows: Row[];
  rowKey: (row: Row) => string;
  firstColumn: { header: string; render: (row: Row) => ReactNode };
  columns: ChartDataGridColumn<Row>[];
  footer?: { label: string; cells: ReactNode[] } | undefined;
}) {
  return (
    <details className="ed-chart-table">
      <summary>View as table</summary>
      <div className="ed-chart-table__scroll">
        <table>
          <caption>{caption}</caption>
          <thead>
            <tr>
              <th scope="col">{firstColumn.header}</th>
              {columns.map((c) => (
                <th key={c.header} scope="col" className={c.numeric ? 'ed-chart-table__num' : undefined}>{c.header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={rowKey(row)}>
                <th scope="row">{firstColumn.render(row)}</th>
                {columns.map((c) => (
                  <td key={c.header} className={c.numeric ? 'ed-chart-table__num' : undefined}>{c.render(row)}</td>
                ))}
              </tr>
            ))}
          </tbody>
          {footer && (
            <tfoot>
              <tr>
                <th scope="row">{footer.label}</th>
                {footer.cells.map((cell, i) => (
                  <td key={columns[i]?.header ?? i} className={columns[i]?.numeric ? 'ed-chart-table__num' : undefined}>{cell}</td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </details>
  );
}
